import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Redo2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { store } from "../../store/index.ts";

/** Session history controls. Dataset writes remain serialized by the store. */
export function HistoryControls() {
  const { canUndo, canRedo } = useSyncExternalStore(store.subscribe, store.getHistory);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  const run = useCallback(async (direction: "undo" | "redo") => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await store[direction]();
      setError(null);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setError(`Could not ${direction}: ${message}. Your data has not changed.`);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.altKey || !(event.ctrlKey || event.metaKey)) return;
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select, [contenteditable]")) return;
      const key = event.key.toLowerCase();
      const direction = key === "z"
        ? (event.shiftKey ? "redo" : "undo")
        : key === "y" && !event.shiftKey ? "redo" : null;
      if (!direction) return;
      event.preventDefault();
      void run(direction);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [run]);

  return (
    <div className="flex items-center gap-0.5 border-r border-budget-rule pr-1.5 mr-1.5">
      <Button type="button" variant="ghost" size="icon-sm" aria-label="Undo"
        title="Undo (Ctrl/Cmd+Z)" disabled={busy || !canUndo} onClick={() => void run("undo")}>
        <Undo2 aria-hidden="true" />
      </Button>
      <Button type="button" variant="ghost" size="icon-sm" aria-label="Redo"
        title="Redo (Ctrl/Cmd+Shift+Z)" disabled={busy || !canRedo} onClick={() => void run("redo")}>
        <Redo2 aria-hidden="true" />
      </Button>
      {error && <p role="alert" className="absolute right-4 top-full max-w-[calc(100vw-2rem)] rounded-b-md border border-t-0 border-destructive/40 bg-background px-3 py-2 text-sm text-destructive shadow-sm sm:right-6">{error}</p>}
    </div>
  );
}
